// Session via the API's Auth0 BFF (api.../api/v1/auth/*):
//  - GET /auth/me ALWAYS answers 200; `authenticated: false` means "log in".
//  - Login is a full-page navigation to GET /auth/login?return_to=<relative path>;
//    the API anchors the path to its DASHBOARD_ORIGIN after the Auth0 round trip.
//  - A failed login comes back as `?login_error=<reason>` (closed vocabulary):
//    shown, never auto-retried (an automatic redirect would loop).
//  - A 401 from any later call drops the app back to the login screen.
//  - Logout is two steps: POST /auth/logout ends OUR session (CSRF-protected); fetch cannot follow its
//    redirect to the Auth0 tenant, so the whole page then navigates to GET /auth/logout-provider, which
//    redirects to the tenant logout and back to the dashboard. A flag in sessionStorage tells the boot after
//    that round trip to show the logged-out screen instead of starting a new login.
//  - Only an Auth0 `sub` on the API's OWNER_SUBS can obtain a session, so there
//    is no "logged in but not the owner" state on this side: that case arrives
//    as `login_error=not_authorized`.
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { getMe, postLogout } from '../api/endpoints';
import { ApiError, loginUrl, onUnauthorized, providerLogoutUrl, setCsrfToken } from '../api/client';
import { isMockMode } from '../dev/mode';
import { errorText } from '../i18n/reasons.hu';
import type { Me } from '../api/model';

export type SessionState =
  | { status: 'unknown' }
  | { status: 'authenticated'; me: Me }
  | { status: 'unauthenticated'; loginError: string | null }
  | { status: 'loggedout' }
  | { status: 'error'; message: string };

interface SessionValue {
  state: SessionState;
  retry: () => void;
  logout: () => void;
  login: () => void;
  /** Hungarian text when the last logout attempt failed (the session is then still alive). */
  logoutError: string | null;
}

const Ctx = createContext<SessionValue | null>(null);

/** The path (+ query, minus our own `login_error`) to come back to after login. */
export function returnTarget(): string {
  const q = new URLSearchParams(window.location.search);
  q.delete('login_error');
  const qs = q.toString();
  return `${window.location.pathname}${qs ? `?${qs}` : ''}`;
}

export function redirectToLogin(): void {
  window.location.assign(loginUrl(returnTarget()));
}

const LOGGED_OUT_FLAG = 'mf_logged_out';

function loggedOutFlag(op: 'set' | 'clear' | 'get'): boolean {
  try {
    if (op === 'set') window.sessionStorage.setItem(LOGGED_OUT_FLAG, '1');
    else if (op === 'clear') window.sessionStorage.removeItem(LOGGED_OUT_FLAG);
    else return window.sessionStorage.getItem(LOGGED_OUT_FLAG) === '1';
  } catch {
    /* storage can be blocked: the flag is a convenience, the login screen still works */
  }
  return false;
}

function readLoginError(): string | null {
  try {
    return new URLSearchParams(window.location.search).get('login_error');
  } catch {
    return null;
  }
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<SessionState>({ status: 'unknown' });
  const [token, setToken] = useState(0);
  const [logoutError, setLogoutError] = useState<string | null>(null);

  useEffect(() => {
    const ac = new AbortController();
    getMe(ac.signal)
      .then((me) => {
        if (ac.signal.aborted) return;
        if (me) {
          loggedOutFlag('clear');
          setCsrfToken(me.csrf);
          setState({ status: 'authenticated', me });
        } else if (loggedOutFlag('get') && readLoginError() === null) {
          // back from the provider logout: stay logged out until the owner chooses to log in
          loggedOutFlag('clear');
          setState({ status: 'loggedout' });
        } else {
          setState({ status: 'unauthenticated', loginError: readLoginError() });
        }
      })
      .catch((e: unknown) => {
        if (ac.signal.aborted) return;
        setState({ status: 'error', message: e instanceof ApiError ? errorText(e.status, e.reason) : errorText(null, null) });
      });
    return () => ac.abort();
  }, [token]);

  useEffect(() => onUnauthorized(() => setState((s) => (s.status === 'loggedout' ? s : { status: 'unauthenticated', loginError: null }))), []);

  // Production: an unauthenticated visitor goes straight to the login, unless
  // the login just failed (then the reason is shown) or they chose to log out.
  useEffect(() => {
    if (state.status === 'unauthenticated' && state.loginError === null && !import.meta.env.DEV) redirectToLogin();
  }, [state]);

  const retry = useCallback(() => {
    setState({ status: 'unknown' });
    setToken((t) => t + 1);
  }, []);

  const logout = useCallback(() => {
    setLogoutError(null);
    postLogout()
      .then(() => {
        setCsrfToken(null);
        if (isMockMode()) {
          setState({ status: 'loggedout' });
          return;
        }
        // Our session is gone; now end the Auth0 tenant session too (a top-level navigation, so the browser follows the redirect).
        loggedOutFlag('set');
        window.location.assign(providerLogoutUrl());
      })
      .catch((e: unknown) => {
        // Not logged out: say so instead of pretending (the session cookie is still valid).
        setLogoutError(e instanceof ApiError ? errorText(e.status, e.reason) : errorText(null, null));
      });
  }, []);

  return <Ctx.Provider value={{ state, retry, logout, login: redirectToLogin, logoutError }}>{children}</Ctx.Provider>;
}

export function useSession(): SessionValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useSession must be used within a SessionProvider');
  return v;
}
